// src/components/api/useAuthedApi.ts
import { API_BASE } from "../../config";
import { useAuth } from "../../auth/AuthProvider";
import { trimTrailingSlash, joinUrl } from "../../lib/url";
import { reportPaymentRequired } from "../../lib/api-client";

export type RunPromptResponse = { ok: boolean; session: string; result: string; updated_at: number; };
export type RunPromptBody = { template: string; system_instruction?: string; };

export function useAuthedApi() {
  const auth: any = useAuth();
  const ensureToken = auth.ensureToken as () => Promise<string>;

  const V2 = trimTrailingSlash(API_BASE || "");

  const buildHeaders = async (json: boolean) => {
    const token = (await ensureToken()) || "";
    const h: Record<string, string> = {};
    if (token) {
      h.Authorization = `Bearer ${token}`;
    }
    if (json) h["Content-Type"] = "application/json";
    return h;
  };

  const safeText = async (r: Response) => { try { return await r.text(); } catch { return `${r.status} ${r.statusText}`; } };

  const apiGet = async <T,>(path: string): Promise<T> => {
    const url = joinUrl(V2, path);
    const r = await fetch(url, { method: "GET", headers: await buildHeaders(false) });
    if (!r.ok) throw new Error(await safeText(r));
    return r.json();
  };

  const apiPost = async <T,>(path: string, body: any): Promise<T> => {
    const url = joinUrl(V2, path);
    const r = await fetch(url, { method: "POST", headers: await buildHeaders(true), body: JSON.stringify(body) });
    const txt = await safeText(r);
    if (!r.ok) {
      reportPaymentRequired(r.status, txt); // prompt writes are entitlement-gated
      throw new Error(txt);
    }
    return JSON.parse(txt) as T;
  };

  const apiPut = async <T,>(path: string, body: any): Promise<T> => {
    const url = joinUrl(V2, path);
    const r = await fetch(url, { method: "PUT", headers: await buildHeaders(true), body: JSON.stringify(body) });
    const txt = await safeText(r);
    if (!r.ok) {
      reportPaymentRequired(r.status, txt);
      throw new Error(txt);
    }
    return JSON.parse(txt) as T;
  };

  const apiDelete = async <T,>(path: string): Promise<T> => {
    const url = joinUrl(V2, path);
    const r = await fetch(url, { method: "DELETE", headers: await buildHeaders(false) });
    const txt = await safeText(r);
    if (!r.ok) {
      reportPaymentRequired(r.status, txt);
      throw new Error(txt);
    }
    return JSON.parse(txt) as T;
  };

  const runPrompt = async (sessionId: string, body: RunPromptBody): Promise<RunPromptResponse> => {
    if (!sessionId) throw new Error("Missing sessionId for /prompts/run");
    if (!body?.template?.trim()) throw new Error("Missing template for /prompts/run");

    // Async tick/poll via HTTP API v2
    const token = (await ensureToken()) || "";
    const postUrl = joinUrl(V2, `/prompts/run?session=${encodeURIComponent(sessionId)}`);
    const postResp = await fetch(postUrl, {
      method: "POST",
      headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
      body: JSON.stringify(
        body.system_instruction
          ? { template: body.template, system_instruction: body.system_instruction }
          : { template: body.template }
      ),
    });
    if (!postResp.ok) throw new Error(await safeText(postResp));
    const { run_id } = await postResp.json();
    if (!run_id) throw new Error("No run_id returned");

    // Poll for result
    const start = Date.now();
    const timeoutMs = 120_000;
    const intervalMs = 2000;
    return new Promise<RunPromptResponse>((resolve, reject) => {
      const iv = setInterval(async () => {
        try {
          if (Date.now() - start > timeoutMs) {
            clearInterval(iv);
            reject(new Error("Timeout waiting for prompt result"));
            return;
          }
          const freshToken = (await ensureToken()) || "";
          if (!freshToken) {
            clearInterval(iv);
            reject(new Error("Authentication expired during polling"));
            return;
          }
          const pollUrl = joinUrl(V2, `/prompts/run?run_id=${encodeURIComponent(run_id)}`);
          const r = await fetch(pollUrl, {
            headers: { Authorization: `Bearer ${freshToken}` },
          });
          if (!r.ok) {
            clearInterval(iv);
            reject(new Error(`Poll ${r.status}: ${await r.text().catch(() => "")}`));
            return;
          }
          const data = await r.json();
          if (data.status === "done") {
            clearInterval(iv);
            const parsed = typeof data.result === "string" ? JSON.parse(data.result) : data.result;
            resolve(parsed as RunPromptResponse);
          } else if (data.status === "error") {
            clearInterval(iv);
            reject(new Error(data.error || "Worker error"));
          }
        } catch (e) {
          clearInterval(iv);
          reject(e);
        }
      }, intervalMs);
    });
  };

  return { apiGet, apiPost, apiPut, apiDelete, runPrompt };
}
